import { type InferResponseType, unwrap } from "@nexxt/api-client";
import type { ListDelegatedAccessInput } from "@nexxt/contracts/identity";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the delegated access (ADR-262, ADR-263): the tokens and connections of the signed-in user and what
// they may carry; every token and connection for the administrator, by cursor, the clients of the MCP server and the
// users without it.

const identity = api.identity;

export type DelegatedAccess = InferResponseType<(typeof identity.me)["delegated-access"]["$get"], 200>["items"][number];
export type DelegationOptions = InferResponseType<(typeof identity.me)["delegation-options"]["$get"], 200>;
export type DelegatedAccessFilters = Pick<ListDelegatedAccessInput, "status" | "kind">;

const keys = { all: ["delegated-access"] as const };

export const delegatedAccessQueries = {
  ...keys,

  mine: () =>
    queryOptions({
      queryKey: [...keys.all, "mine"],
      queryFn: ({ signal }) => unwrap(identity.me["delegated-access"].$get({}, { init: { signal } })),
    }),

  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => unwrap(identity.me["delegation-options"].$get({}, { init: { signal } })),
    }),

  /** The clients of the MCP server, for the administrator (ADR-263). */
  clients: () =>
    queryOptions({
      queryKey: [...keys.all, "clients"],
      queryFn: ({ signal }) => unwrap(identity["oauth-clients"].$get({}, { init: { signal } })),
    }),

  /** The users for whom the access via MCP is turned off (ADR-263). */
  blocks: () =>
    queryOptions({
      queryKey: [...keys.all, "blocks"],
      queryFn: ({ signal }) => unwrap(identity["delegation-blocks"].$get({}, { init: { signal } })),
    }),

  list: ({ status, kind }: DelegatedAccessFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", { status, kind }],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          identity["delegated-access"].$get(
            {
              query: {
                ...(status && { status }),
                ...(kind && { kind }),
                ...(pageParam && { cursor: pageParam }),
                limit: "50",
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),
};
